<script setup lang="ts">
// 2026-10-07 (Paolo): Charteris (silver) top strip — the Gold strip's template tied to silver futures, in the
// BGA-vs-NAS sparkline format: CHARTERIS (recent official NAVs) · SI (silver futures since the previous New York
// close — the 08:40 decision measure — with the dashed 08:40 UK marker) · GC (gold futures, same view: the miners are
// part gold) · FX (GBP/USD, the fund is valued in sterling) · K_t (live synthetic NAV, Phase 4). No Treasuries
// (Paolo), no vol index. SI/GC/FX/CHARTERIS tiles open /charts/charterisvssi; the K_t tile opens the K_t tab.
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, fetchCharterisSynthNavLive, type CharterisIntradayResponse, type CharterisDailyRow, type CharterisSynthNavLive } from '../../api/client'
import { sparkPoints, sparkPathD } from '../sparkline'
import { findMarkerIndex } from '../japan/niyMarker'

const W = 80
const H = 18
const CHART = '/charts/charterisvssi'

const intraday = ref<CharterisIntradayResponse | null>(null)
const daily = ref<CharterisDailyRow[]>([])
const liveKt = ref<CharterisSynthNavLive | null>(null)
let timer: ReturnType<typeof setInterval> | null = null

// Each feed is independent: a failed call leaves its own tiles on dashes, never the whole strip.
function safe<T>(f: () => Promise<T>): Promise<T | null> {
  try { return f().catch(() => null) } catch { return Promise.resolve(null) }
}

async function load() {
  const [i, d, k] = await Promise.all([
    safe(() => api.charterisChartsIntraday('1d')),
    safe(() => api.charterisChartsDaily('1m')),
    safe(() => fetchCharterisSynthNavLive()),
  ])
  intraday.value = i
  daily.value = d?.rows ?? []
  liveKt.value = k
}

onMounted(async () => {
  await load()
  timer = setInterval(load, 60_000)
})
onUnmounted(() => { if (timer) clearInterval(timer) })

function path(values: number[]): string {
  return sparkPathD(sparkPoints(values, W, H))
}
function lastTwoPct(values: number[]): number | null {
  return values.length >= 2 ? (values[values.length - 1] / values[values.length - 2] - 1) * 100 : null
}

const navValues = computed(() => daily.value.slice(-20).map(r => r.navPence))
const navPct = computed(() => lastTwoPct(navValues.value))

function futures(key: 'si' | 'gc') {
  const bars = computed(() => (intraday.value?.rows ?? []).filter(r => r[key] != null)
    .map(r => ({ tsUtc: r.tsUtc, value: r[key] as number })))
  const values = computed(() => bars.value.map(b => b.value))
  const pct = computed(() => {
    const anchor = key === 'si' ? intraday.value?.siAnchor : intraday.value?.gcAnchor
    const last = values.value.at(-1)
    return last != null && anchor ? (last / anchor - 1) * 100 : null
  })
  const markerX = computed(() => {
    if (bars.value.length < 2) return null
    const idx = findMarkerIndex(bars.value)
    return idx < 0 ? null : (idx / (bars.value.length - 1)) * W
  })
  return { values, pct, markerX }
}
const si = futures('si')
const gc = futures('gc')

const fxValues = computed(() => daily.value.filter(r => r.gbpUsd != null).map(r => r.gbpUsd as number))
const fxPct = computed(() => lastTwoPct(fxValues.value))

function fmtPct(v: number | null): string {
  if (v == null) return '—'
  return (v >= 0 ? '+' : '') + v.toFixed(2) + '%'
}
function returnClass(v: number | null): string {
  if (v == null) return ''
  return v >= 0 ? 'positive' : 'negative'
}
const ktTierClass = computed(() => {
  const t = liveKt.value?.tier
  // 'closed' = COMEX metals shut (weekend / daily halt): a neutral dot, the estimate is the last close (2026-10-10).
  return t === 'live' ? 'kt-live' : t === 'degraded' ? 'kt-degraded' : t === 'critical' ? 'kt-critical' : t === 'closed' ? 'kt-closed' : 'kt-none'
})
function goKt() {
  if (window.location.hash.replace('#', '') === 'kt') return
  window.location.hash = 'kt'  // Dashboard's hashchange listener switches activeTab to the K_t tab
}
function goChart() {
  if (window.location.pathname === CHART) return
  window.history.pushState({}, '', CHART)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
</script>

<template>
  <div class="sparkline-bar" data-testid="charteris-sparkline-bar">
    <div class="spark-item" data-testid="spark-charteris" title="Charteris official NAV — last 20 publications" @click="goChart">
      <span class="spark-label">CHARTERIS</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <path v-if="navValues.length > 1" :d="path(navValues)" class="ln nav" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(navPct)">{{ fmtPct(navPct) }}</span>
    </div>

    <div class="spark-item" data-testid="spark-si" title="Silver futures (SI) since the previous New York close — the 08:40 decision measure" @click="goChart">
      <span class="spark-label si">SI</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-if="si.markerX.value != null" data-testid="si-marker-0840" :x1="si.markerX.value" :x2="si.markerX.value" y1="0" y2="18" class="marker" />
        <path v-if="si.values.value.length > 1" :d="path(si.values.value)" class="ln si" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(si.pct.value)">{{ fmtPct(si.pct.value) }}</span>
    </div>

    <div class="spark-item" data-testid="spark-gc" title="Gold futures (GC) since the previous New York close" @click="goChart">
      <span class="spark-label gc">GC</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-if="gc.markerX.value != null" data-testid="gc-marker-0840" :x1="gc.markerX.value" :x2="gc.markerX.value" y1="0" y2="18" class="marker" />
        <path v-if="gc.values.value.length > 1" :d="path(gc.values.value)" class="ln gc" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(gc.pct.value)">{{ fmtPct(gc.pct.value) }}</span>
    </div>

    <div class="spark-item" data-testid="spark-fx" title="GBP/USD daily — the fund is valued in sterling" @click="goChart">
      <span class="spark-label fx">FX</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <path v-if="fxValues.length > 1" :d="path(fxValues)" class="ln fx" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(fxPct)">{{ fmtPct(fxPct) }}</span>
    </div>

    <div class="spark-item" data-testid="spark-kt" title="Live Charteris synthetic NAV (K_t) — opens the K_t tab" @click="goKt">
      <span class="spark-label kt">K_t</span>
      <span v-if="liveKt" class="kt-dot" :class="ktTierClass" :title="liveKt.tier" />
      <span class="spark-value kt-val" :class="returnClass(liveKt ? liveKt.return_pct : null)">{{ liveKt ? fmtPct(liveKt.return_pct) : '—' }}</span>
    </div>
  </div>
</template>

<style scoped>
/* Mirrors SparklineBar / GoldSparklineBar so the strip reads identically across funds. */
.sparkline-bar {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  gap: 16px;
  padding: 6px 12px;
  background: #0d1117;
  border: 1px solid #1c2128;
  border-radius: 6px;
  margin-bottom: 8px;
}
.spark-item { display: flex; align-items: center; gap: 6px; min-width: 0; cursor: pointer; padding: 2px 4px; border-radius: 4px; }
.spark-item:hover { background: #161b22; }
.spark-svg { flex: 1 1 auto; min-width: 0; width: 100%; height: 18px; }
.spark-label { font-size: 10px; color: #c0c7d0; font-family: 'JetBrains Mono', monospace; min-width: 24px; flex: 0 0 auto; }
.spark-label.si { color: #b0bec5; }
.spark-label.gc { color: #f6c542; }
.spark-label.fx { color: #26c6da; }
.spark-label.kt { color: #a78bfa; }
.kt-val { flex: 1 1 auto; text-align: right; }
.kt-dot { width: 7px; height: 7px; border-radius: 50%; flex: 0 0 auto; }
.kt-dot.kt-live { background: #4caf50; }
.kt-dot.kt-degraded { background: #d29922; }
.kt-dot.kt-critical { background: #f44336; }
.kt-dot.kt-closed { background: #7c8db5; }
.kt-dot.kt-none { background: #6e7681; }
.spark-value { font-size: 10px; font-family: 'JetBrains Mono', monospace; color: #888; min-width: 40px; flex: 0 0 auto; }
.spark-value.positive { color: #4caf50; }
.spark-value.negative { color: #f44336; }
.ln { stroke-width: 1.2; vector-effect: non-scaling-stroke; }
.ln.nav { stroke: #4caf50; }
.ln.si { stroke: #b0bec5; }
.ln.gc { stroke: #f6c542; }
.ln.fx { stroke: #26c6da; }
.marker { stroke: #8b949e; stroke-width: 1; stroke-dasharray: 2, 2; vector-effect: non-scaling-stroke; }

@media (max-width: 768px) {
  .sparkline-bar { grid-template-columns: repeat(3, 1fr); gap: 8px; padding: 4px 8px; }
  .spark-label { font-size: 9px; }
  .spark-value { font-size: 9px; }
}
</style>
